
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserProfile } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface UserFormProps {
  formData: {
    email: string;
    password: string;
    full_name: string;
    role: 'admin' | 'manager' | 'staff';
  };
  setFormData: (data: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  isEdit?: boolean;
  editingUser?: any; // User being edited
}

const UserForm: React.FC<UserFormProps> = ({
  formData,
  setFormData,
  onSubmit,
  loading,
  isEdit = false,
  editingUser
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  
  // Check if editing current user
  const isEditingCurrentUser = isEdit && editingUser && currentUser && editingUser.user_id === currentUser.id;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">{t('users.email')}</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required={!isEdit}
          disabled={isEdit}
          className="text-base"
        />
      </div>
      {!isEdit && (
        <div className="space-y-2">
          <Label htmlFor="password">{t('users.password')}</Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            className="text-base"
          />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor={isEdit ? "edit_full_name" : "full_name"}>{t('users.fullName')}</Label>
        <Input
          id={isEdit ? "edit_full_name" : "full_name"}
          type="text"
          value={formData.full_name}
          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          required
          className="text-base"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={isEdit ? "edit_role" : "role"}>{t('users.role')}</Label>
        <Select 
          value={formData.role} 
          onValueChange={(value: any) => setFormData({ ...formData, role: value })}
          disabled={isEditingCurrentUser}
        >
          <SelectTrigger className="text-base">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="staff">{t('users.staff')}</SelectItem>
            <SelectItem value="manager">{t('users.manager')}</SelectItem>
            <SelectItem value="admin">{t('users.admin')}</SelectItem>
          </SelectContent>
        </Select>
        {isEditingCurrentUser && (
          <p className="text-sm text-muted-foreground">
            {t('users.cannotChangeOwnRole')}
          </p>
        )}
      </div>
      {!isEdit && (
        <div className="bg-blue-50 p-3 rounded-md">
          <p className="text-sm text-blue-800">
            {t('users.emailVerificationNote')}
          </p>
        </div>
      )}
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? (isEdit ? t('users.updating') : t('users.creating')) : (isEdit ? t('users.updateUser') : t('users.createUser'))}
      </Button>
    </form>
  );
};

export default UserForm;
